import React, { useState } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { Mail, MessageCircle, Send, Phone, MapPin, Clock } from 'lucide-react';

const ContactUs = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeMethod, setActiveMethod] = useState('email');
  const [formData, setFormData] = useState({
    name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
    email: user?.email || '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Send contact form to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSuccess(true);
      setFormData({
        name: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '',
        email: user?.email || '',
        subject: '',
        message: '',
      });

      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = () => {
    // Navigate to messages or open a chat modal
    window.location.href = '/messages';
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '20px' : '40px 20px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h1 style={{
          fontSize: isMobile ? '32px' : '48px',
          marginBottom: '16px',
          color: 'var(--primary)',
        }}>
          Contact Us
        </h1>
        <p style={{
          fontSize: isMobile ? '16px' : '18px',
          color: 'var(--textSecondary)',
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          Have a question or need help? We're here for you! Reach out via email or chat.
        </p>
      </div>

      {/* Contact Method Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginBottom: '40px',
      }}>
        <button
          onClick={() => setActiveMethod('email')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: activeMethod === 'email' ? 'var(--primary)' : 'var(--surface)',
            color: activeMethod === 'email' ? 'white' : 'var(--text)',
            border: `2px solid ${activeMethod === 'email' ? 'var(--primary)' : 'var(--border)'}`,
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <Mail size={20} />
          Email Us
        </button>
        <button
          onClick={() => setActiveMethod('chat')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: activeMethod === 'chat' ? 'var(--primary)' : 'var(--surface)',
            color: activeMethod === 'chat' ? 'white' : 'var(--text)',
            border: `2px solid ${activeMethod === 'chat' ? 'var(--primary)' : 'var(--border)'}`,
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <MessageCircle size={20} />
          Live Chat
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gap: '32px',
      }}>
        {/* Main Content Area */}
        <div>
          {activeMethod === 'email' ? (
            // Email Form
            <div style={{
              background: 'var(--cardBackground)',
              padding: isMobile ? '24px' : '32px',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-md)',
            }}>
              <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Send us an Email</h2>

              {success && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #c3e6cb',
                }}>
                  ✓ Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {error && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #f5c6cb',
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '16px',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}>
                    Your Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '16px',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help?"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '16px',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    fontSize: '14px',
                  }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us what's on your mind..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '14px',
                    background: loading ? 'var(--border)' : 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  <Send size={20} />
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          ) : (
            // Chat Option
            <div style={{
              background: 'var(--cardBackground)',
              padding: isMobile ? '24px' : '32px',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-md)',
              textAlign: 'center',
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'var(--primary)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}>
                <MessageCircle size={40} color="white" />
              </div>

              <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>Start a Live Chat</h2>
              <p style={{
                color: 'var(--textSecondary)',
                marginBottom: '32px',
                fontSize: '16px',
              }}>
                {isAuthenticated
                  ? 'Chat with our support team in real-time through our messaging system.'
                  : 'Please log in to start a chat with our support team.'}
              </p>

              {isAuthenticated ? (
                <button
                  onClick={handleStartChat}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 32px',
                    background: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  <MessageCircle size={20} />
                  Open Messages
                </button>
              ) : (
                <a
                  href="/login"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 32px',
                    background: 'var(--primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    textDecoration: 'none',
                  }}
                >
                  Log In to Chat
                </a>
              )}
            </div>
          )}
        </div>

        {/* Contact Info Sidebar */}
        <div>
          <div style={{
            background: 'var(--cardBackground)',
            padding: isMobile ? '24px' : '32px',
            borderRadius: '12px',
            boxShadow: 'var(--shadow-md)',
          }}>
            <h3 style={{ fontSize: '20px', marginBottom: '24px' }}>Other Ways to Reach Us</h3>

            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--secondary)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Mail size={20} color="var(--primary)" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Email</div>
                  <a
                    href="mailto:support@babyresell.co.site"
                    style={{ color: 'var(--primary)', textDecoration: 'none', fontSize: '14px' }}
                  >
                    support@babyresell.co.site
                  </a>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--secondary)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Phone size={20} color="var(--primary)" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Phone</div>
                  <a
                    href="tel:+1234567890"
                    style={{ color: 'var(--primary)', textDecoration: 'none', fontSize: '14px' }}
                  >
                    +1 (234) 567-890
                  </a>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--secondary)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <MapPin size={20} color="var(--primary)" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Address</div>
                  <p style={{ fontSize: '14px', color: 'var(--textSecondary)', margin: 0 }}>
                    Federal Way, WA 98023
                  </p>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'var(--secondary)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Clock size={20} color="var(--primary)" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>Business Hours</div>
                  <p style={{ fontSize: '14px', color: 'var(--textSecondary)', margin: 0 }}>
                    Mon-Fri: 9am - 6pm PST<br />
                    Sat-Sun: 10am - 4pm PST
                  </p>
                </div>
              </div>
            </div>

            <div style={{
              padding: '16px',
              background: 'var(--secondary)',
              borderRadius: '8px',
              marginTop: '24px',
            }}>
              <p style={{
                fontSize: '13px',
                color: 'var(--textSecondary)',
                margin: 0,
                textAlign: 'center',
              }}>
                💡 <strong>Quick Tip:</strong> Check our FAQ section for instant answers to common questions!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
